import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Hero rotation of featured Sea Star Studio videos.
 *
 * Renders ONLY when at least one video is flagged `show_on_homepage` in
 * the admin. Auto-advances every 12 s (or when each clip ends). All
 * playback is muted + autoplay-safe so browser policies never block it.
 */
export default function FeaturedVideoHero() {
  const [videos, setVideos] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/videos/featured").then(({ data }) => setVideos(data || [])).catch(() => setVideos([]));
  }, []);

  useEffect(() => {
    if (videos.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % videos.length), 12000);
    return () => clearInterval(t);
  }, [videos.length]);

  if (!videos.length) return null;
  const v = videos[idx];

  return (
    <section className="relative max-w-6xl mx-auto px-4 md:px-6 -mt-2 mb-10" data-testid="featured-video-hero">
      <div className="relative rounded-[28px] overflow-hidden border-4 border-[#7fcfc7] shadow-lg bg-black aspect-video">
        <video
          key={v.id}
          src={v.url}
          autoPlay
          muted
          playsInline
          loop={videos.length === 1}
          onEnded={() => setIdx((i) => (i + 1) % videos.length)}
          className="w-full h-full object-cover"
          data-testid={`featured-video-${v.id}`}
        />
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
          <div className="bg-black/55 backdrop-blur-sm rounded-2xl px-3 py-1.5 text-white">
            <div className="text-xs uppercase tracking-wider opacity-70">Now playing</div>
            <div className="font-bold text-sm">{v.title}</div>
          </div>
          {videos.length > 1 && (
            <div className="flex gap-1.5">
              {videos.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Show clip ${i + 1}`}
                  className={`w-2.5 h-2.5 rounded-full border-2 border-white ${i === idx ? "bg-white" : "bg-transparent"}`}
                  data-testid={`featured-video-dot-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
